<template>
  <div class="w-full h-full overflow-hidden">
    <!-- Navbar -->
    <nav
      class="relative flex flex-wrap items-center justify-between px-6 py-4 bg-white border-b border-gray-200 shadow-xl md:left-0 md:block md:fixed md:top-0 md:bottom-0 md:flex-row md:flex-no-wrap md:overflow-hidden md:w-64"
    >
      <!-- Backdrop -->
      <div
        v-show="isSidebarOpen"
        class="fixed inset-0 z-10 opacity-50 bg-fs-300 md:hidden"
        aria-hidden="true"
        @click="hideSidebar"
      />
      <div
        class="grid w-full grid-cols-3 px-0 mx-auto md:flex-col md:items-stretch md:min-h-full md:flex-no-wrap"
      >
        <!-- Toggler -->
        <div class="flex">
          <button
            class="px-3 py-1 text-xl leading-none text-black bg-transparent border border-transparent border-solid rounded opacity-50 cursor-pointer md:hidden"
            type="button"
            @click.prevent="toggleSidebar"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
        <!-- Brand -->
        <div
          class="flex justify-center text-sm font-bold text-left text-gray-700 uppercase whitespace-no-wrap"
        >
          <a
            class="inline-block"
            href="https://facestudio.online/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              class="w-20"
              :src="logo"
              alt="FaceStudio"
            >
          </a>
        </div>
      </div>
    </nav>

    <!-- Sidebar -->
    <aside
      class="fixed top-0 left-0 z-30 flex flex-col items-stretch w-64 h-full px-6 py-3 overflow-auto transition-all duration-300 ease-in-out transform bg-white border-r border-gray-200"
      :class="
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      "
    >
      <!-- Header -->
      <div class="flex mb-4">
        <div
          class="flex-grow inline-block mr-0 text-sm font-bold text-left text-gray-700 uppercase whitespace-no-wrap md:block md:pb-2"
        >
          <a
            class="inline-block"
            href="https://facestudio.online/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              class="w-20"
              :src="logo"
              alt="FaceStudio"
            >
          </a>
        </div>
        <button
          type="button"
          class="p-2 text-xl leading-none text-black bg-transparent border border-transparent border-solid rounded opacity-50 cursor-pointer md:hidden"
          @click="hideSidebar"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Navigation Links -->
      <ul class="flex flex-col">
        <li class="items-center w-full">
          <router-link
            to="/"
            :exact="true"
            class="flex items-center pt-2 pb-3 space-x-3"
            active-class="text-fs-500 hover:text-fs-600"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            <p>{{ "SidebarLink_Home" | i18n }}</p>
          </router-link>
        </li>
        <li class="items-center w-full">
          <router-link
            to="/schedule"
            :exact="true"
            class="flex items-center pt-2 pb-3 space-x-2"
            active-class="text-fs-500 hover:text-fs-600"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p>{{ "SidebarLink_Schedule" | i18n }}</p>
          </router-link>
        </li>
        <li class="items-center w-full">
          <router-link
            :to="{
              path: '/messengers',
              query: { email: user.email, phone: user.phone || '' }
            }"
            :exact="true"
            class="flex items-center pt-2 pb-3 space-x-2"
            active-class="text-fs-500 hover:text-fs-600"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <p>{{ "SidebarLink_Chatbots" | i18n }}</p>
          </router-link>
        </li>
        <li class="items-center w-full">
          <router-link
            to="/help"
            :exact="true"
            class="flex items-center pt-2 pb-3 space-x-2"
            active-class="text-fs-500 hover:text-fs-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <p>{{ "SidebarLink_HelpCenter" | i18n }}</p>
          </router-link>
        </li>
      </ul>

      <ul class="flex flex-col">
        <p class="mt-6 mb-2 text-xs font-semibold text-gray-500 uppercase">
          {{ "SidebarTitle_ContactUs" | i18n }}
        </p>
        <li
          v-if="$options.filters.i18n('SidebarLink_Email').length"
          class="items-center w-full"
        >
          <a
            :href="`mailto:${$options.filters.i18n('SidebarLink_Email')}`"
            class="flex items-center pt-2 pb-3 space-x-3"
          >
            <p>{{ "SidebarLink_Email" | i18n }}</p>
          </a>
        </li>
        <li
          v-if="$options.filters.i18n('SidebarLink_Phone').length"
          class="items-center w-full"
        >
          <a
            :href="`tel:${$options.filters.i18n('SidebarLink_Phone')}`"
            class="flex items-center pt-2 pb-3 space-x-3"
          >
            <p>{{ "SidebarLink_Phone" | i18n }}</p>
          </a>
        </li>
      </ul>

      <ul
        v-if="employee"
        class="flex flex-col"
      >
        <p class="mt-6 mb-2 text-xs font-semibold text-gray-500 uppercase">
          {{ "SidebarTitle_Employee" | i18n }}
        </p>
        <li class="items-center w-full">
          <router-link
            to="/admin/events"
            :exact="true"
            class="flex items-center pt-2 pb-3 space-x-3"
            active-class="text-fs-500 hover:text-fs-600"
          >
            <p>{{ "SidebarLink_Events" | i18n }}</p>
          </router-link>
        </li>
        <li class="items-center w-full">
          <router-link
            to="/admin/courses"
            :exact="true"
            class="flex items-center pt-2 pb-3 space-x-3"
            active-class="text-fs-500 hover:text-fs-600"
          >
            <p>{{ "SidebarLink_Courses" | i18n }}</p>
          </router-link>
        </li>
        <li class="items-center w-full">
          <router-link
            to="/admin/payments"
            :exact="true"
            class="flex items-center pt-2 pb-3 space-x-3"
            active-class="text-fs-500 hover:text-fs-600"
          >
            <p>{{ "SidebarLink_Payments" | i18n }}</p>
          </router-link>
        </li>
        <li class="items-center w-full">
          <router-link
            to="/admin/promocodes"
            :exact="true"
            class="flex items-center pt-2 pb-3 space-x-3"
            active-class="text-fs-500 hover:text-fs-600"
          >
            <p>{{ "SidebarLink_Promocodes" | i18n }}</p>
          </router-link>
        </li>
        <li class="items-center w-full">
          <router-link
            to="/admin/forms"
            :exact="true"
            class="flex items-center pt-2 pb-3 space-x-3"
            active-class="text-fs-500 hover:text-fs-600"
          >
            <p>{{ "SidebarLink_Forms" | i18n }}</p>
          </router-link>
        </li>
      </ul>

      <!-- Footer: User -->
      <div class="mt-auto mb-4">
        <div class="flex items-center space-x-2">
          <div class="flex items-center justify-center text-sm text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-8 h-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="flex-auto truncate">
            <span class="font-semibold">
              {{ user.name }}
            </span>
            <br>{{ user.email }}
          </div>
          <button
            type="button"
            class="p-2 text-gray-500"
            @click="signOut"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

const logo = require("@/assets/icons/logo.svg");

export default {
  name: "Navbar",

  data: () => ({
    logo,
    isSidebarOpen: false
  }),

  computed: {
    ...mapGetters(["user", "employee"])
  },

  watch: {
    $route() {
      this.hideSidebar();
    }
  },

  methods: {
    hideScroll(s) {
      const body = document.getElementsByTagName("body")[0];
      s ? (body.style.overflow = "hidden") : (body.style.overflow = "auto");
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
      this.hideScroll(true);
    },
    hideSidebar() {
      this.isSidebarOpen = false;
      this.hideScroll(false);
    },
    async signOut() {
      await this.$store.dispatch("logout");
      this.$router.push("/login");
    }
  }
};
</script>
